import type { InviteCaptureRequirements, MemberAccessEntryTypeOption } from './api';

export interface VisitorContactRule {
  mobile: { show: boolean; required: boolean };
  email: { show: boolean; required: boolean };
  /** Neither is required on its own, but one of the two must be given. */
  eitherRequired: boolean;
}

/**
 * Which visitor contact the server insists on for this access type. Mirrors
 * `AccessInviteService.validateMemberInviteVisitorContact` and the capture requirements, so the
 * form's labels and checks never disagree with what the create call enforces.
 */
export function resolveVisitorContactRule(
  type: MemberAccessEntryTypeOption | undefined,
  capture: InviteCaptureRequirements,
): VisitorContactRule {
  // 1. The invite goes out on whichever channels its message template supports.
  const byEmail = type?.notify_supports_email !== false;
  const bySms = type?.notify_supports_sms !== false;

  // 2. A field is required when the access type asks for it, or when it is the template's only
  //    channel. The gate texting the visitor needs a mobile whatever the template does.
  const mobileRequired =
    capture.phone.required || (bySms && !byEmail) || type?.gate_notify_supports_sms === true;
  const emailRequired = capture.email.required || (byEmail && !bySms);

  // 3. A template that sends both is satisfied by either, unless one is already required.
  const eitherRequired = byEmail && bySms && !mobileRequired && !emailRequired;

  // 4. The server enforces these whatever the capture settings say, so a field that can satisfy
  //    it is always on the form.
  return {
    mobile: { show: capture.phone.capture || mobileRequired || eitherRequired, required: mobileRequired },
    email: { show: capture.email.capture || emailRequired || eitherRequired, required: emailRequired },
    eitherRequired,
  };
}

/** The field-level message for each contact the rule still needs, given what is typed now. */
export function missingVisitorContact(
  rule: VisitorContactRule,
  mobile: string,
  email: string,
): { mobile?: string; email?: string } {
  // The "one required" message sits under the mobile, the first field of the pair.
  const mobileMessage = rule.mobile.required
    ? 'Enter the visitor mobile number.'
    : rule.eitherRequired && !email
      ? 'Enter a mobile number or an email address.'
      : undefined;

  return {
    mobile: mobile ? undefined : mobileMessage,
    email: rule.email.required && !email ? 'Enter the visitor email address.' : undefined,
  };
}
